import camelCase from 'lodash-es/camelCase';
import kebabCase from 'lodash-es/kebabCase';
import { TFunction, useTranslation } from 'next-i18next/pages';

import { CareType } from '@pflegia/types/api/employer';
import { removeCountryName } from '@pflegia/ui/shared/utils/remove-country-name';
import AnonymousWrapper from '@pflegia/ui/shared/wrapper/AnonymousWrapper';

import { HighlightsList, PerksList } from '@hooks/get-job-position.hook';

import { FUNNEL } from '@shared/application/constants';

import { dateFormat, dateService } from '@utils/dates';

import { SectionTitle } from '../section/Section.component';
import defaultPerks from './DefaultJobPerks';
import SummaryItem from './SummaryItem';

interface SummaryCardProps {
  anonymous?: boolean;
  careType?: CareType;
  formalLanguage?: boolean;
  perks: PerksList;
  alwaysShowTitle?: boolean;
}

const getDate = (t: TFunction, date?: string | string[]): string => {
  if (typeof date === 'string') {
    if (date === 'Immediately') {
      return t(`perks.${date.toLowerCase()}`);
    }
    return dateService.formatDate(date, dateFormat);
  }
  return '';
};

const getEmploymentTypeValues = (employmentTypes: string[], t: TFunction) =>
  Array.isArray(employmentTypes)
    ? employmentTypes
        .map(type => {
          const [typeOfEmployment, hours] = type.split(':');
          const defaultTranslation = t(`candidateFields:workingHours.types.${camelCase(typeOfEmployment)}`);
          return hours && parseInt(hours) > 0 ? `${defaultTranslation} (${hours} ${t('candidateFields:hours')})` : defaultTranslation;
        })
        .join(', ')
    : '';

export const translatedValue = (key: keyof PerksList | keyof HighlightsList, value: string | string[], t: TFunction) => {
  switch (key) {
    case 'startOfWork':
      return getDate(t, value.toString());

    case 'interviewWith':
      return Array.isArray(value) ? value.map(type => t(`perks.${key}.${camelCase(type)}`)).join(', ') : '';

    case 'departments':
      return Array.isArray(value) ? value.map(type => t(`candidateFields:${key}.${camelCase(type)}`)).join(', ') : '';

    case 'contractTypes':
      return Array.isArray(value) ? value.join(', ') : '';

    case 'employmentType':
      return getEmploymentTypeValues(value as string[], t);

    case 'typeOfHospital':
      return typeof value === 'string' ? t(`hospitalType.${value.toLowerCase()}`) : '';

    case 'averageTime':
      return `${value} ${t(`perks.minutes`)}`;

    case 'typeOfIntensiveCare':
      return typeof value === 'string' ? t(`intensiveCareType.${camelCase(value)}`) : '';

    case 'carForPrivateUse':
      return t(`carForPrivateUse.${value}`);

    case 'offerApartments':
    case 'adjustableDistance':
      return t(value);

    default:
      return Array.isArray(value) ? value.join(', ') : value || '';
  }
};

const getPerkKey = (perks: PerksList, key: keyof PerksList): keyof PerksList => {
  switch (key) {
    case 'numberOfBeds':
      return perks.numberOfPatients ? 'numberOfPatients' : 'numberOfBeds';

    default:
      return key;
  }
};

const getLabelFromKey = (key: keyof PerksList, formalLanguage: boolean) => {
  switch (key) {
    case 'salary':
      return formalLanguage ? 'salaryFormal' : 'salary';

    default:
      return key;
  }
};

const SummaryCard = ({
  anonymous = false,
  formalLanguage = false,
  perks,
  alwaysShowTitle = false,
}: SummaryCardProps): React.JSX.Element => {
  const { t } = useTranslation(['jobDetails', 'candidateFields']);

  return (
    <div className='rounded-2xl bg-white p-6 lg:rounded-none lg:px-12'>
      <SectionTitle titleKey='summary.title' t={t} className={alwaysShowTitle ? '' : 'lg:hidden'} level='h2' />
      <div className='lg:hidden'>
        <AnonymousWrapper anonymous={anonymous} href={FUNNEL}>
          <div className='mb-6 flex flex-col gap-6'>
            {perks.employer && (
              <SummaryItem
                data-testid='employer-name-mobile'
                emoji={defaultPerks.employer}
                key={t('perks.employer')}
                label={t('perks.employer')}
                value={perks.employer}
              />
            )}
            {perks.address && (
              <SummaryItem
                data-testid='employer-address-mobile'
                emoji={defaultPerks.address}
                key={t('perks.address')}
                label={t('perks.address')}
                value={removeCountryName(perks.address)}
              />
            )}
          </div>
        </AnonymousWrapper>
      </div>
      <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
        {(Object.entries(perks) as [keyof PerksList, string[] | string][])
          .filter(([key]) => !['address', 'employer', anonymous ? undefined : 'postalCode'].includes(key))
          .map(([key, value]) => {
            if (!value) return null;

            const finalKey = getPerkKey(perks, key);

            return (
              <SummaryItem
                data-testid={kebabCase(finalKey)}
                emoji={defaultPerks[finalKey]}
                label={t(`perks.${getLabelFromKey(finalKey, formalLanguage)}`)}
                value={translatedValue(finalKey, value, t)}
                summaryKey={finalKey}
                key={finalKey}
              />
            );
          })}
      </div>
    </div>
  );
};

export default SummaryCard;
